import { create, StoreApi, UseBoundStore } from 'zustand'

import { SelectedFilters } from '@/components/table/base/FilterableTable/FilterableTable.types'
import { STORE_STATUS } from '@/constants/store'
import logger from '@/utils/_legacy/logger'
import { notifyError } from '@/utils/notifyError'

import { useUiStore } from '../useUiStore'
import type {
  BaseTableState,
  CreateTableStoreConfig,
  FetchApiArgs,
  FetchData,
  FullStoreActions,
  FullStoreState
} from './table.types'

export function createBaseTableStore<
  TData,
  TSortAttribute extends string = string,
  TFilterParamName extends string = string,
  TFetchParams = unknown,
  TStoreExtensionState = object,
  TStoreExtensionActions = object
>(
  config: CreateTableStoreConfig<
    TData,
    TSortAttribute,
    TFilterParamName,
    TFetchParams,
    TStoreExtensionState,
    TStoreExtensionActions
  >
): UseBoundStore<
  StoreApi<
    FullStoreState<TData, TSortAttribute, TFilterParamName, TStoreExtensionState, TFetchParams> &
      FullStoreActions<TSortAttribute, TFilterParamName, TFetchParams, TStoreExtensionActions>
  >
> {
  const {
    storeName,
    initialStateOverrides,
    defaultSort = null,
    fetchApi,
    features = { pagination: false, sorting: false, filtering: false },
    onError,
    customInitialState = {} as TStoreExtensionState,
    customActions
  } = config

  const baseInitialState: BaseTableState<TData, TSortAttribute, TFilterParamName, TFetchParams> = {
    data: [],
    pagination: {
      page: 0,
      pageSize: useUiStore.getState().tablePageSize,
      total: 0
    },
    lastFetchParams: initialStateOverrides?.lastFetchParams || (undefined as TFetchParams),
    sort: defaultSort,
    filters: initialStateOverrides?.initialFilters || ({} as SelectedFilters<TFilterParamName>),
    status: STORE_STATUS.initial,
    error: null,
    abortController: null,
    ...(initialStateOverrides || {})
  }

  const fullInitialState: FullStoreState<
    TData,
    TSortAttribute,
    TFilterParamName,
    TStoreExtensionState,
    TFetchParams
  > = {
    ...baseInitialState,
    ...customInitialState
  }

  return create<
    FullStoreState<TData, TSortAttribute, TFilterParamName, TStoreExtensionState, TFetchParams> &
      FullStoreActions<TSortAttribute, TFilterParamName, TFetchParams, TStoreExtensionActions>
  >((set, get) => {
    const fetchData = (async (params: unknown) => {
      const state = get()
      state.abortController?.abort(`New fetch initiated for ${storeName}`)
      const newAbortController = new AbortController()

      set(
        (s) =>
          ({
            abortController: newAbortController,
            status:
              s.status === STORE_STATUS.initial ? STORE_STATUS.loading : STORE_STATUS.fetching,
            error: null
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          }) as any
      )

      try {
        const apiArgs: FetchApiArgs<TFetchParams, TSortAttribute, TFilterParamName> = {
          page: features.pagination ? state.pagination.page : 0,
          pageSize: features.pagination ? state.pagination.pageSize : -1,
          sort: features.sorting ? state.sort : undefined,
          filters: features.filtering ? state.filters : ({} as SelectedFilters<TFilterParamName>),
          signal: newAbortController.signal,
          additionalParams: params as TFetchParams,
          getState: get,
          setState: set,
          storeName
        }

        const { content, totalElements, number, size, ...rest } = await fetchApi(apiArgs)

        set(
          (currentState) =>
            ({
              data: content,
              pagination: features.pagination
                ? {
                    page: number ?? currentState.pagination.page,
                    pageSize: size ?? currentState.pagination.pageSize,
                    total: totalElements
                  }
                : currentState.pagination,
              status: STORE_STATUS.idle,
              abortController: null,
              lastFetchParams: params as TFetchParams,
              ...rest
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error?.name === 'CanceledError' || newAbortController.signal.aborted) {
          logger.warn(`Fetch aborted - ${storeName}. Reason: ${newAbortController.signal.reason}`)
          if (get().abortController === newAbortController) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            set({ abortController: null, status: STORE_STATUS.idle } as any)
          }
          return
        }
        logger.error(`Failed to fetch ${storeName}:`, error)
        notifyError(error)
        if (onError) {
          onError(error, storeName)
        }

        set({
          status: STORE_STATUS.error,
          error: error.message || `Failed to fetch ${storeName}`,
          abortController: null
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)
      }
    }) as FetchData<TFetchParams>

    return {
      ...fullInitialState,

      fetchData,

      setFilters: async (newFilters) => {
        if (!features.filtering) {
          logger.warn(`Filtering is not enabled for ${storeName} store.`)
          return
        }
        set(
          (state) =>
            ({
              filters: newFilters,
              pagination: { ...state.pagination, page: 0 }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any
        )
        await get().fetchData(get().lastFetchParams)
      },

      setSort: async (newSort) => {
        if (!features.sorting) {
          logger.warn(`Sorting is not enabled for ${storeName} store.`)
          return
        }
        set(
          (state) =>
            ({
              sort: newSort,
              pagination: { ...state.pagination, page: 0 }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any
        )
        await get().fetchData(get().lastFetchParams)
      },

      setPagination: async (paginationUpdate) => {
        if (!features.pagination) {
          logger.warn(`Pagination is not enabled for ${storeName} store.`)
          return
        }
        set(
          (state) =>
            ({
              pagination: { ...state.pagination, ...paginationUpdate }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any
        )
        await get().fetchData(get().lastFetchParams)
      },

      resetState: () => {
        get().abortController?.abort(`State reset for ${storeName}`)
        const newPageSize = useUiStore.getState().tablePageSize

        let filtersForReset =
          initialStateOverrides?.initialFilters || ({} as SelectedFilters<TFilterParamName>)
        if (features.filtering) {
          filtersForReset = get().filters
        }

        let sortForReset = defaultSort
        if (features.sorting) {
          sortForReset = get().sort || null
        }

        const resetStateCore = {
          ...customInitialState,
          data: [],
          pagination: {
            page: 0,
            pageSize: newPageSize,
            total: 0
          },
          sort: sortForReset,
          filters: filtersForReset,
          status: STORE_STATUS.initial,
          error: null,
          abortController: null
        }

        const finalResetState = {
          ...resetStateCore,
          ...(initialStateOverrides || {}),
          data: [],
          pagination: resetStateCore.pagination,
          sort: sortForReset,
          filters: filtersForReset,
          status: STORE_STATUS.initial,
          error: null,
          abortController: null
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        set(finalResetState as any)
      },

      cancelFetch: (reason?: string) => {
        const ac = get().abortController
        if (ac) {
          ac.abort(reason || `Manual cancellation for ${storeName}`)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          set({ abortController: null, status: STORE_STATUS.idle } as any)
        }
      },

      ...(customActions ? customActions(set, get) : ({} as TStoreExtensionActions))
    }
  })
}
